"use client";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Image from "next/image";
import Link from "next/link";
import { createInvoice } from "@/serverActions/signin";
import toast from "react-hot-toast";

const schema = z.object({
  name: z.string().min(1, { message: "Name ist ein Pflichtfeld" }),
  email: z.string().email({ message: "Keine gültige E-Mail-Adresse" }),
  message: z
    .string()
    .min(10, { message: "Bitte stelle den Club in ein/zwei Sätzen vor" }),
});

export type FormData = {
  name: string;
  email: string;
  message: string;
};

export default function Page() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      await createInvoice(data);
    } catch (e) {
      toast.error("Etwas ist schief gelaufen. Bitte versuch es erneut", {
        position: "top-right",
      });
      setIsSubmitting(false);
      return;
    }

    toast.success("Erfolgreich angemeldet", { position: "top-right" });
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col items-center justify-center px-6 lg:h-screen lg:gap-y-12 ">
      <Link href="/" className="my-6 flex items-center gap-x-1 lg:my-0">
        <Image src="logo.svg" alt="logo" width={150} height={150} />
      </Link>
      <div className="flex text-white rounded-lg border border-gray-200 bg-green-600 shadow-md flex-col md:flex-row w-full md:max-w-[1024px] [&amp;>img]:hidden md:[&amp;>img]:w-96 md:[&amp;>img]:p-0 md:[&amp;>*]:w-full md:[&amp;>*]:p-16 lg:[&amp;>img]:block">
        <img
          alt=""
          className="h-96 w-full rounded-t-lg object-cover md:h-auto md:max-w-[325px] md:rounded-none md:rounded-l-lg"
          src="/login.jpg"
        />
        <div className="flex h-full w-full flex-col justify-center gap-4 p-6 md:min-w-[400px]">
          <h1 className="mb-3 text-2xl font-bold  md:text-3xl">
            Kostenlos anmelden
          </h1>
          <p>
            Wir freuen uns, dass du dich für unsere Community interessierst!
          </p>

          <h3>Wichtig:</h3>
          <p>
            Um die Qualität unserer Community sicherzustellen, werden alle Daten
            manuell geprüft. Dies kann in Einzelfällen etwas Zeit in Anspruch
            nehmen. Wir bitten dich daher um etwas Geduld.
          </p>

          <b>Wir freuen uns auf dich!</b>
          <form onSubmit={handleSubmit(onSubmit)}>
            <label className="form-control w-full mb-3">
              <input
                type="text"
                placeholder="Name"
                className="input input-bordered w-full bg-white text-black"
                {...register("name")}
              />

              {errors.name ? (
                <div className="label">
                  <span className="label-text-alt text-white">
                    {errors.name.message}
                  </span>
                </div>
              ) : null}
            </label>

            <label className="form-control w-full mb-3">
              <input
                type="text"
                placeholder="E-Mail"
                className="input input-bordered w-full bg-white text-black"
                {...register("email")}
              />

              {errors.email ? (
                <div className="label">
                  <span className="label-text-alt text-white">
                    {errors.email.message}
                  </span>
                </div>
              ) : null}
            </label>

            <label className="form-control mb-3">
              <textarea
                className="textarea textarea-bordered h-24  bg-white text-black"
                placeholder="Nachricht"
                {...register("message")}
              />

              {errors.message ? (
                <div className="label">
                  <span className="label-text-alt text-white">
                    {errors.message.message}
                  </span>
                </div>
              ) : null}
            </label>

            <div className="flex justify-end">
              <button
                type="submit"
                className="btn btn-accent"
                disabled={isSubmitting}
              >
                Abschicken
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
